import React, { useState, useEffect } from "react";
import RoomIcon from "@mui/icons-material/Room";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Card, CardContent, Typography, Button, CircularProgress, Box,TextField } from "@mui/material";
const LocationFetcher = ({ onLocationFetched }) => {
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [address, setAddress] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(null); // 'granted', 'denied', 'prompt'
  const [manualLocation, setManualLocation] = useState("");

  // Check for HTTPS (geolocation needs HTTPS)
  const isSecure = window.location.protocol === 'https:';

  // Main fetching logic with fallback to lower accuracy
  const fetchLocation = (highAccuracy = true) => {
    setLoading(true);
    setError(null);

    if (!isSecure) {
      setError("Geolocation requires a secure (HTTPS) connection.");
      setLoading(false);
      return;
    }

    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser.");
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setLatitude(lat);
        setLongitude(lng);

        // Reverse geocode
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
          );
          const data = await response.json();
          setAddress(data);
          if (onLocationFetched) {
            onLocationFetched({
              latitude: lat,
              longitude: lng,
              accuracy: position.coords.accuracy,
              address: data,
            });
          }
        } catch {
          setError("Could not fetch address. Network error?");
          setAddress(null);
        }
        setLoading(false);
      },
      (err) => {
        // Try fallback to low accuracy if timeout or position unavailable
        if (highAccuracy && (err.code === err.TIMEOUT || err.code === err.POSITION_UNAVAILABLE)) {
          fetchLocation(false);
        } else {
          // Provide instructions based on error code
          if (err.code === err.PERMISSION_DENIED) {
            setError("Location permission denied. Please enable location access in your browser or device settings.");
          } else if (err.code === err.POSITION_UNAVAILABLE) {
            setError("Position unavailable. Make sure location services/GPS are enabled.");
          } else if (err.code === err.TIMEOUT) {
            setError("Location timeout. Try again or enter location manually.");
          } else {
            setError("Unknown error: " + err.message);
          }
          setLoading(false);
        }
      },
      {
        enableHighAccuracy: highAccuracy,
        timeout: 12000,
        maximumAge: 0,
      }
    );
  };

  const checkPermissionStatus = async () => {
    if (!isSecure) {
      setError("Geolocation requires HTTPS connection.");
      return;
    }
    if (navigator.permissions) {
      try {
        const result = await navigator.permissions.query({ name: "geolocation" });
        setPermission(result.state);
        if (result.state === "granted") {
          fetchLocation(true);
        }
        result.onchange = () => {
          setPermission(result.state);
        };
      } catch {
        fetchLocation(true); // fallback
      }
    } else {
      fetchLocation(true);
    }
  };

  useEffect(() => {
    checkPermissionStatus();
  }, []);

  // Manual location fallback
  const handleManualLocation = () => {
    const matches = manualLocation.match(/([-+]?[\d.]+)[,\s]+([-+]?[\d.]+)/);
    if (matches) {
      const lat = parseFloat(matches[1]);
      const lng = parseFloat(matches[2]);
      setLatitude(lat);
      setLongitude(lng);
      setAddress({ display_name: "Manual entry" });
      if (onLocationFetched) {
        onLocationFetched({
          latitude: lat,
          longitude: lng,
          accuracy: null,
          address: { display_name: "Manual entry" },
        });
      }
      setError(null);
    } else {
      setError("Enter coordinates as 'latitude, longitude' (e.g. 28.72, 77.06)");
    }
  };

  return (
    <Box sx={{ borderRadius: 2, p: 1, backgroundColor: "rgba(245, 245, 245, 0.5)", border: "1px solid rgba(0, 0, 0, 0.08)", display: "inline-flex", alignItems: "center", gap: 1, maxWidth: "100%" }}>
      {loading && <CircularProgress size={16} thickness={5} />}

      {error && (
        <Box>
          <Typography variant="caption" color="error">{error}</Typography>
          {/* Manual entry fallback */}
          <Box sx={{ mt: 1 }}>
            <TextField size="small" placeholder="latitude, longitude" value={manualLocation} onChange={e => setManualLocation(e.target.value)} />
            <Button variant="outlined" size="small" sx={{ ml: 1 }} onClick={handleManualLocation}>
              Enter Manually
            </Button>
          </Box>
        </Box>
      )}

      {(permission === "prompt" || permission === "denied") && !loading && (
        <Button variant="outlined" size="small" onClick={() => fetchLocation(true)} startIcon={<MyLocationIcon />}>
          Get Location
        </Button>
      )}

      {latitude && longitude && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <MyLocationIcon sx={{ color: "primary.main", fontSize: "16px" }} />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="caption">{latitude.toFixed(4)}, {longitude.toFixed(4)}</Typography>
            {address && <Typography variant="caption" color="text.secondary">{address.display_name ? address.display_name.split(",").slice(0,2).join(", ") : "Manual entry"}</Typography>}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LocationFetcher;
